import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import {
  deleteDocument,
  documentUrl,
  fetchItemAttachments,
  uploadItemAttachment,
  type ProjectDocument,
} from '@/features/documents/api'
import { formatSize } from '@/features/documents/size'
import {
  attachLabel,
  fetchCodeLinks,
  requestTriage,
  respondToTriage,
  type CodeLink,
  type TriageSuggestion,
  createLabel,
  detachLabel,
  fetchLabels,
  fetchWatchers,
  setWatching,
  type Label,
} from '@/features/work-items/api'
import type { ApiError } from '@/lib/api/client'

/**
 * The three things a task collects that the panel used to leave out: the files
 * people attach to it, the labels they file it under, and who is following it.
 *
 * They sit together because each is a small list with one control, and giving
 * each its own section of the panel would push the conversation off the screen.
 */
export function TaskExtras({ workItemId, viewerId }: { workItemId: string; viewerId: string }) {
  return (
    <div className="border-line mt-5 flex flex-col gap-4 border-t pt-4">
      <Files workItemId={workItemId} />
      <Labels workItemId={workItemId} />
      <Watch workItemId={workItemId} viewerId={viewerId} />
      <Code workItemId={workItemId} />
      <Triage workItemId={workItemId} />
    </div>
  )
}

function Files({ workItemId }: { workItemId: string }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)

  const files = useQuery<ProjectDocument[], ApiError>({
    queryKey: ['item-attachments', workItemId],
    queryFn: () => fetchItemAttachments(workItemId),
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['item-attachments', workItemId] })

  const upload = useMutation<ProjectDocument, ApiError, File>({
    mutationFn: (file) => uploadItemAttachment(workItemId, file, 'internal'),
    onSuccess: () => void refresh(),
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteDocument(id),
    onSuccess: () => void refresh(),
  })

  const download = useMutation<string, ApiError, string>({
    mutationFn: (id) => documentUrl(id),
    onSuccess: (url) => window.open(url, '_blank', 'noopener'),
  })

  return (
    <section>
      <div className="mb-1.5 flex items-center gap-2">
        <h3 className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
          {t('view.files')}
        </h3>
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={upload.isPending}
          className="text-brand-600 ms-auto flex items-center gap-1 text-[11px] disabled:opacity-40"
        >
          <Icon name="upload" className="size-3" />
          {t('view.addFile')}
        </button>
        <input
          ref={input}
          type="file"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file !== undefined) upload.mutate(file)
            event.target.value = ''
          }}
        />
      </div>

      {upload.isError ? (
        <p className="mb-1 text-[11px] text-[color:var(--color-state-blocked)]">
          {upload.error.message}
        </p>
      ) : null}

      {files.data === undefined || files.data.length === 0 ? (
        <p className="text-ink-500 text-[12px]">{t('view.noFiles')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {files.data.map((file) => (
            <li key={file.id} className="flex items-center gap-2 text-[12px]">
              <Icon name="file" className="text-ink-500 size-3.5 shrink-0" />
              <button
                type="button"
                onClick={() => download.mutate(file.id)}
                className="text-ink-900 min-w-0 flex-1 truncate text-start"
              >
                {file.name}
              </button>
              <span className="text-ink-500 shrink-0 font-mono text-[10px]">
                {formatSize(file.sizeBytes, i18n.language)}
              </span>
              <button
                type="button"
                onClick={() => remove.mutate(file.id)}
                title={t('document.delete')}
                aria-label={t('document.delete')}
                className="text-ink-500 hover:text-[color:var(--color-state-blocked)] shrink-0 rounded p-0.5"
              >
                <Icon name="trash" className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Labels({ workItemId }: { workItemId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')

  const labels = useQuery<Label[], ApiError>({
    queryKey: ['labels'],
    queryFn: fetchLabels,
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['labels'] })
    void queryClient.invalidateQueries({ queryKey: ['work-items'] })
  }

  const attach = useMutation<void, ApiError, string>({
    mutationFn: (labelId) => attachLabel(workItemId, labelId),
    onSuccess: () => {
      setAdding(false)
      refresh()
    },
  })

  // A label nobody has made yet is the common case on a young board, so
  // creating one and applying it is a single action rather than a detour.
  const create = useMutation<Label, ApiError, string>({
    mutationFn: (label) => createLabel(label),
    onSuccess: (label) => {
      setName('')
      attach.mutate(label.id)
    },
  })

  const detach = useMutation<void, ApiError, string>({
    mutationFn: (labelId) => detachLabel(workItemId, labelId),
    onSuccess: refresh,
  })

  return (
    <section>
      <div className="mb-1.5 flex items-center gap-2">
        <h3 className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
          {t('view.labels')}
        </h3>
        <button
          type="button"
          onClick={() => setAdding((open) => !open)}
          className="text-brand-600 ms-auto flex items-center gap-1 text-[11px]"
        >
          <Icon name="plus" className="size-3" />
          {t('view.addLabel')}
        </button>
      </div>

      {adding ? (
        <form
          className="mb-1.5 flex items-center gap-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            const existing = (labels.data ?? []).find(
              (label) => label.name.toLowerCase() === name.trim().toLowerCase(),
            )
            if (existing !== undefined) attach.mutate(existing.id)
            else if (name.trim() !== '') create.mutate(name.trim())
          }}
        >
          <input
            value={name}
            autoFocus
            list="known-labels"
            onChange={(event) => setName(event.target.value)}
            className="border-line bg-surface min-w-0 flex-1 rounded border px-2 py-1 text-[12px] outline-none focus:border-[color:var(--color-brand-300)]"
          />
          <datalist id="known-labels">
            {(labels.data ?? []).map((label) => (
              <option key={label.id} value={label.name} />
            ))}
          </datalist>
          <button
            type="submit"
            disabled={create.isPending || attach.isPending}
            className="bg-brand-600 rounded px-2 py-1 text-[11px] text-white disabled:opacity-40"
          >
            {t('view.newLabel')}
          </button>
        </form>
      ) : null}

      {create.isError || attach.isError ? (
        <p className="mb-1 text-[11px] text-[color:var(--color-state-blocked)]">
          {(create.error ?? attach.error)?.message}
        </p>
      ) : null}

      <ul className="flex flex-wrap gap-1.5">
        {(labels.data ?? []).map((label) => (
          <li key={label.id}>
            <button
              type="button"
              onClick={() => detach.mutate(label.id)}
              title={t('view.delete')}
              className="border-line text-ink-700 hover:bg-surface-2 flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
            >
              <span
                aria-hidden="true"
                className="size-1.5 rounded-full"
                style={{ backgroundColor: label.color ?? 'var(--color-ink-500)' }}
              />
              {label.name}
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Watch({ workItemId, viewerId }: { workItemId: string; viewerId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const watchers = useQuery<{ id: string; name: string }[], ApiError>({
    queryKey: ['watchers', workItemId],
    queryFn: () => fetchWatchers(workItemId),
    retry: false,
  })

  const watching = (watchers.data ?? []).some((person) => person.id === viewerId)

  const toggle = useMutation<void, ApiError, boolean>({
    mutationFn: (next) => setWatching(workItemId, next),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['watchers', workItemId] }),
  })

  return (
    <section className="flex items-center gap-2">
      <h3 className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
        {t('view.watchers')}
      </h3>
      <span className="text-ink-700 text-[12px]">
        {(watchers.data ?? []).map((person) => person.name).join(' · ') || '—'}
      </span>
      <button
        type="button"
        onClick={() => toggle.mutate(!watching)}
        disabled={toggle.isPending}
        className={`ms-auto flex shrink-0 items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${
          watching ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-line text-ink-500'
        }`}
      >
        <Icon name="people" className="size-3" />
        {watching ? t('view.watching') : t('view.watch')}
      </button>
    </section>
  )
}

/**
 * Commits and pull requests the webhooks matched to this task by its key.
 *
 * The matching already happened — every push is parsed for AIS-14 and linked —
 * and the result had nowhere to appear, so the connection between a board and
 * the code that closes it was invisible.
 */
function Code({ workItemId }: { workItemId: string }) {
  const { t } = useTranslation()

  const links = useQuery<CodeLink[], ApiError>({
    queryKey: ['code-links', workItemId],
    queryFn: () => fetchCodeLinks(workItemId),
    retry: false,
  })

  if (links.data === undefined) return null

  return (
    <section>
      <h3 className="text-ink-500 mb-1.5 font-mono text-[10px] tracking-widest uppercase">
        {t('view.codeLinks')}
      </h3>

      {links.data.length === 0 ? (
        <p className="text-ink-500 text-[12px]">{t('view.noCodeLinks')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {links.data.map((link) => (
            <li key={link.id} className="flex items-center gap-2 text-[12px]">
              <Icon name="link" className="text-ink-500 size-3.5 shrink-0" />
              <span className="text-ink-500 shrink-0 font-mono text-[10px] uppercase">{link.kind}</span>
              {link.url === null ? (
                <span className="text-ink-900 min-w-0 flex-1 truncate">{link.title ?? link.reference}</span>
              ) : (
                <a href={link.url} target="_blank" rel="noreferrer" className="text-brand-600 min-w-0 flex-1 truncate">
                  {link.title ?? link.reference}
                </a>
              )}
              {link.state === null ? null : (
                <span className="text-ink-500 shrink-0 font-mono text-[10px]">{link.state}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * The model's opinion on what this task is, which a person then accepts or
 * dismisses. It proposes; it never decides, and it never writes a number
 * (ADR-0007).
 */
function Triage({ workItemId }: { workItemId: string }) {
  const { t } = useTranslation()
  const [suggestion, setSuggestion] = useState<TriageSuggestion | null>(null)

  const ask = useMutation<TriageSuggestion, ApiError, void>({
    mutationFn: () => requestTriage(workItemId),
    onSuccess: (result) => setSuggestion(result),
  })

  const respond = useMutation<TriageSuggestion, ApiError, 'accepted' | 'dismissed'>({
    mutationFn: (status) => respondToTriage(workItemId, suggestion?.id ?? '', status),
    onSuccess: () => setSuggestion(null),
  })

  return (
    <section className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => ask.mutate()}
        disabled={ask.isPending}
        className="text-brand-600 flex items-center gap-1.5 self-start text-[11px] disabled:opacity-40"
      >
        <Icon name="sparkle" className="size-3" />
        {t('view.triage')}
      </button>

      {ask.isError ? (
        <p className="text-[11px] text-[color:var(--color-state-blocked)]">{ask.error.message}</p>
      ) : null}

      {suggestion === null ? null : (
        <div className="border-line bg-surface-2 flex flex-col gap-1.5 rounded border p-2">
          <ul className="text-ink-700 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px]">
            {suggestion.type === null ? null : (
              <li>
                <span className="text-ink-500">{t('work.task')}</span> {suggestion.type}
              </li>
            )}
            {suggestion.priority === null ? null : (
              <li>
                <span className="text-ink-500">{t('work.priority')}</span>{' '}
                {t(`priority.${suggestion.priority}`)}
              </li>
            )}
            {/* A proportion, not a figure the model wrote: the server computes
                it, so ADR-0007 is satisfied by where it comes from. */}
            <li className="text-ink-500 font-mono tabular-nums">
              {Math.round(suggestion.confidence * 100)}%
            </li>
          </ul>
          {suggestion.reason === null ? null : (
            <p className="text-ink-700 text-[12px]">{suggestion.reason}</p>
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => respond.mutate('accepted')} className="bg-brand-600 rounded px-2 py-0.5 text-[11px] text-white">
              {t('view.triageAccept')}
            </button>
            <button type="button" onClick={() => respond.mutate('dismissed')} className="text-ink-500 px-1.5 py-0.5 text-[11px]">
              {t('view.triageDismiss')}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
